import { ProgressIndicator } from "@fluentui/react";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { localize } from "src/l10n";
import videojs, { VideoJsPlayer } from "video.js";
import "video.js/dist/video-js.css";
import "./VideoJSPlayer.scss";
import postMediaPlayCountUpdate from "src/utils/postMediaPlayCountUpdate";
import RealtimeContext from "src/contexts/RealtimeContext/RealtimeContext";

interface IProps {
    videoUrl: string;
    thumbnailUrl?: string;
    fill?: boolean;
    uberId?: number;
    onReady?: (player: VideoJsPlayer) => void;
}

export const VideoJSPlayer = (props: IProps) => {
    const videoRef = useRef(null);
    const playerRef = useRef<VideoJsPlayer>(null);
    const activeUrlRef = useRef(props.videoUrl);
    const activeThumbnailRef = useRef(props.thumbnailUrl);
    const { onReady } = props;
    const [unsupportedMedia, setUnsupportedMedia] = useState<boolean>(false);
    const { subscribe, unsubscribe } = useContext(RealtimeContext);

    const onPlay = useCallback(() => {
        if (!props.uberId) {
            return;
        }

        postMediaPlayCountUpdate(props.uberId, props.videoUrl);
    }, [props.videoUrl, props.uberId]);

    useEffect(() => {
        activeUrlRef.current = props.videoUrl;
        activeThumbnailRef.current = props.thumbnailUrl;
    }, [props.videoUrl, props.thumbnailUrl]);

    useEffect(() => {
        if (!playerRef.current) {
            const videoElement = document.createElement("video-js");

            videoElement.classList.add("vjs-big-play-centered");
            videoRef.current.appendChild(videoElement);

            const player = (playerRef.current = videojs(
                videoElement,
                {
                    sources: [
                        {
                            src: activeUrlRef.current,
                            type: activeUrlRef.current.endsWith("m3u8") ? "application/x-mpegURL" : "video/mp4",
                        },
                    ],
                    poster: activeThumbnailRef.current,
                    controls: true,
                    fill: props.fill,
                    fluid: !props.fill,
                    preload: "metadata",
                    responsive: true,
                },
                () => {
                    onReady?.(player);
                }
            ));

            player.on("play", onPlay);

            player.on("error", () => {
                const error = player.error();

                if (error?.code === error?.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                    setUnsupportedMedia(true);
                }
            });
        } else {
            const player = playerRef.current;

            if (unsupportedMedia) {
                setUnsupportedMedia(false);
            }

            player.off("play");

            player.src({
                src: activeUrlRef.current,
                type: activeUrlRef.current.endsWith("m3u8") ? "application/x-mpegURL" : "video/mp4",
            });

            player.poster(activeThumbnailRef.current);

            player.on("play", onPlay);
        }
    }, [videoRef, onReady, props.videoUrl, props.thumbnailUrl, props.fill, props.uberId, onPlay]);

    useEffect(() => {
        const player = playerRef.current;

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);

    // extract partial Bunny video GUID prefix (first two segments) for matching realtime events.
    // match after a / to avoid matching hex inside the bcdn_token value.
    const videoGuidPrefix = props.videoUrl.match(/\/([0-9a-f]{8}-[0-9a-f]{4})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\//i)?.[1];

    useEffect(() => {
        if (!videoGuidPrefix) {
            return;
        }

        const onVideoReady = (message: { videoGuidPrefix: string; accessToken: string }) => {
            if (message.videoGuidPrefix !== videoGuidPrefix) {
                return;
            }

            if (!unsupportedMedia) {
                return;
            }

            const player = playerRef.current;
            if (!player || player.isDisposed()) {
                return;
            }

            // reconstruct signed url
            const currentUrl = new URL(props.videoUrl);
            const guid = props.videoUrl.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i)?.[1];
            const tokenPath = encodeURIComponent(`/${guid}/`);
            const newUrl = `${currentUrl.origin}/${message.accessToken}&token_path=${tokenPath}/${guid}/playlist.m3u8`;
            const newThumbnail = `${currentUrl.origin}/${message.accessToken}&token_path=${tokenPath}/${guid}/thumbnail.jpg`;

            setUnsupportedMedia(false);

            activeUrlRef.current = newUrl;
            activeThumbnailRef.current = newThumbnail;

            player.error(null);
            player.poster(newThumbnail);
            player.src({
                src: newUrl,
                type: "application/x-mpegURL",
            });
        };

        subscribe("Bunny:VideoReady", onVideoReady);
        return () => unsubscribe("Bunny:VideoReady", onVideoReady);
    }, [videoGuidPrefix, props.videoUrl, unsupportedMedia, subscribe, unsubscribe]);

    return (
        <div
            data-vjs-player
            className={classNames("VideoJSPlayer ignore-custom-font", {
                "unsupported-media": unsupportedMedia,
            })}
        >
            {unsupportedMedia && <ProgressIndicator label={localize("BearbetningPagar")} />}
            <div ref={videoRef} />
        </div>
    );
};

export default VideoJSPlayer;
